// The on-device copy of buddy conversations. The cloud keeps posts only while a shared streak lives
// (cloudflare/src/sharedStreak.ts); each member's device keeps its own copy afterwards. Merging a page from
// the server: posts the server still has replace the local copy; a local post missing from the server is
//  - kept and marked archived when it is older than `clearedAt` (the streak ended: it left the cloud),
//  - kept as it is when it is older than the page the server sent (simply not loaded yet),
//  - dropped otherwise (its author deleted it while it was still shared).
// Photos are not kept on the device: archived posts lose their photo.
import type { SocialPost } from '../types';

export const MAX_ARCHIVED = 300;

export function mergeFeed(local: SocialPost[], live: SocialPost[], opts: { clearedAt: number; complete: boolean }): SocialPost[] {
  const liveIds = new Set(live.map(p => p.id));
  const oldestLive = opts.complete || !live.length ? -Infinity : Math.min(...live.map(p => p.createdAt));
  const kept = local.filter(p => !liveIds.has(p.id)).flatMap(p => {
    if (p.archived || p.createdAt < opts.clearedAt) return [{ ...p, archived: true, photo: false }];
    if (p.createdAt < oldestLive) return [p];
    return [];
  });
  return [...live, ...kept].sort((a, b) => b.createdAt - a.createdAt || (a.id < b.id ? 1 : -1)).slice(0, MAX_ARCHIVED);
}

/** UTC day of a post, like the streak's days. */
export const postDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/**
 * The meal calendar: posts of the chosen streaks (all of them by default), one entry per post even when it
 * went to several streaks (same batchId), newest day first. `streaks` lists the streaks each post is in.
 */
export function calendar(feeds: Record<string, SocialPost[]>, only?: string): { day: string; posts: (SocialPost & { streaks: string[] })[] }[] {
  const byBatch = new Map<string, SocialPost & { streaks: string[] }>();
  for (const [streakId, posts] of Object.entries(feeds)) {
    if (only && streakId !== only) continue;
    for (const p of posts) {
      const seen = byBatch.get(p.batchId);
      // Prefer a copy the cloud still has (it carries the photo and live reactions).
      if (!seen) byBatch.set(p.batchId, { ...p, streaks: [streakId] });
      else byBatch.set(p.batchId, { ...(seen.archived && !p.archived ? p : seen), streaks: [...seen.streaks, streakId] });
    }
  }
  const days = new Map<string, (SocialPost & { streaks: string[] })[]>();
  for (const p of [...byBatch.values()].sort((a, b) => b.createdAt - a.createdAt)) {
    const day = postDay(p.createdAt);
    days.set(day, [...(days.get(day) ?? []), p]);
  }
  return [...days.entries()].map(([day, posts]) => ({ day, posts }));
}

/** A streak's display name: its own, or the other members' names. */
export function streakName(s: { name: string | null; members: { userId: string; displayName: string }[] }, me: string | null, fallback: string): string {
  if (s.name) return s.name;
  const others = s.members.filter(m => m.userId !== me).map(m => m.displayName);
  return others.length ? others.join(', ') : fallback;
}

/** The download of a streak's copy on this device (text, recipes, reactions; no photos). */
export function streakExport(name: string, posts: SocialPost[], exportedAt: Date): string {
  return JSON.stringify({
    format: 'chefi-streak', version: 1, name, exportedAt: exportedAt.toISOString(),
    days: calendar({ s: posts }).map(({ day, posts: list }) => ({
      day, posts: list.map(p => ({ author: p.author.displayName, at: new Date(p.createdAt).toISOString(), caption: p.caption, recipe: p.recipe ?? undefined, reactions: p.reactions })),
    })),
  }, null, 2);
}
